import { Link, useLocation } from 'react-router-dom';
import { ShoppingBag, Menu, X, Instagram, Mail, ArrowRight } from 'lucide-react';
import { useState, useEffect } from 'react';
import { useCart } from '@/context/CartContext';
import CartSheet from '@/components/CartSheet';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function LogoFull({ size = 'nav', variant = 'dark' }) {
  const isHero = size === 'hero';
  const fontSize = isHero ? 'text-[48px] md:text-[56px]' : 'text-[20px]';
  const menSize = isHero ? 'text-[13px]' : 'text-[7px]';
  const lineH = isHero ? 'h-[52px] md:h-[60px]' : 'h-[22px]';
  const gap = isHero ? 'mx-3 md:mx-4' : 'mx-1.5';
  const isLight = variant === 'light';

  return (
    <div className="flex items-center" data-testid="brand-logo">
      <span className={`font-serif font-bold ${fontSize} leading-none tracking-[0.04em]`}>
        <span style={{ color: isLight ? '#0C4A8C' : '#FFFFFF' }}>TRU</span>
        <span style={{ color: isLight ? '#0C7BC0' : '#7CC4F0' }}>FORM</span>
      </span>
      <span className={`${gap} ${lineH} w-px`} style={{ background: isLight ? '#0C7BC0' : 'rgba(124,196,240,0.7)' }} />
      <span className={`flex flex-col items-center font-sans ${menSize} font-medium tracking-[0.25em] leading-[1.3]`} style={{ color: isLight ? '#0C7BC0' : '#7CC4F0' }}>
        <span>M</span><span>E</span><span>N</span>
      </span>
    </div>
  );
}

function AnnouncementBar() {
  return (
    <div data-testid="announcement-bar" className="bg-white text-[11px] py-2.5 text-center uppercase tracking-[2px] font-sans font-normal" style={{ color: 'rgba(12,74,140,0.65)', borderBottom: '1px solid rgba(12,74,140,0.08)' }}>
      Free shipping above &#8377;499 &middot; Delivered in 3-5 days &middot; truformmen.com
    </div>
  );
}

function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { totalItems, setIsOpen } = useCart();
  const loc = useLocation();
  useEffect(() => setOpen(false), [loc]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const links = [
    { to: '/shop', label: 'Shop' },
    { to: '/science', label: 'The Science' },
    { to: '/about', label: 'About' },
  ];

  return (
    <nav data-testid="navbar" className={`sticky top-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white/95 backdrop-blur-md' : 'bg-white'}`} style={{ borderBottom: '1px solid rgba(12,74,140,0.08)' }}>
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-16">
        <Link to="/" data-testid="logo-link"><LogoFull size="nav" variant="light" /></Link>

        <div className="hidden md:flex items-center gap-10">
          {links.map(l => (
            <Link key={l.to} to={l.to} data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, '-')}-link`}
              className={`text-[11px] font-sans tracking-[3px] uppercase transition-colors ${loc.pathname === l.to ? 'text-[#0C4A8C]' : 'text-[#0C4A8C]/50 hover:text-[#0C4A8C]'}`}>
              {l.label}
            </Link>
          ))}
        </div>

        <div className="flex items-center gap-4">
          <button data-testid="cart-button" onClick={() => setIsOpen(true)}
            className="relative bg-[#0C4A8C] text-white w-9 h-9 rounded-[2px] flex items-center justify-center hover:bg-[#093B70] transition-colors">
            <ShoppingBag size={16} strokeWidth={1.8} />
            {totalItems > 0 && (
              <span data-testid="cart-count" className="absolute -top-1 -right-1 bg-[#0C7BC0] text-white text-[8px] font-sans font-medium w-4 h-4 rounded-full flex items-center justify-center">
                {totalItems}
              </span>
            )}
          </button>
          <button data-testid="mobile-menu-button" className="md:hidden text-[#0C4A8C]/60 hover:text-[#0C4A8C]" onClick={() => setOpen(!open)}>
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="md:hidden bg-white" style={{ borderTop: '1px solid rgba(12,74,140,0.08)' }}>
          <div className="px-6 py-6 flex flex-col gap-5">
            {links.map(l => (
              <Link key={l.to} to={l.to} data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s/g, '-')}-link`}
                className="text-base font-serif text-[#0C4A8C]/60 hover:text-[#0C4A8C] transition-colors">{l.label}</Link>
            ))}
          </div>
        </div>
      )}
      <CartSheet />
    </nav>
  );
}

function FooterNewsletter() {
  const [nlEmail, setNlEmail] = useState('');
  const [nlDone, setNlDone] = useState(false);

  const handleNewsletter = async (e) => {
    e.preventDefault();
    if (!nlEmail) return;
    try {
      await axios.post(`${API}/waitlist`, { email: nlEmail, source: 'newsletter' });
      setNlDone(true); setNlEmail('');
    } catch (err) {
      console.error('Newsletter signup failed:', err);
    }
  };

  return (
    <div>
      <h4 className="eyebrow text-[#BAE6FD] mb-5">Stay in the Loop</h4>
      <p className="text-[12px] text-white/30 font-light mb-3">Get skincare tips & exclusive offers.</p>
      {nlDone ? (
        <p data-testid="newsletter-success" className="text-[12px] text-[#BAE6FD]">You're in! Check your inbox.</p>
      ) : (
        <form onSubmit={handleNewsletter} className="flex gap-0" data-testid="newsletter-form">
          <input
            data-testid="newsletter-email"
            type="email" value={nlEmail} onChange={e => setNlEmail(e.target.value)}
            placeholder="Enter your email"
            required
            className="flex-1 bg-white/5 border border-white/10 text-white text-[12px] px-3 py-2.5 rounded-l-[2px] focus:outline-none focus:border-[#BAE6FD] placeholder-white/20"
          />
          <button data-testid="newsletter-submit" type="submit"
            className="bg-[#BAE6FD] text-[#0C4A8C] px-3 py-2.5 rounded-r-[2px] hover:bg-[#E0F2FE] transition-colors">
            <ArrowRight size={14} />
          </button>
        </form>
      )}
    </div>
  );
}

function Footer() {
  const [about, setAbout] = useState({ instagram_url: 'https://instagram.com/truformmen', email: 'hello@truformmen.com' });

  useEffect(() => {
    axios.get(`${API}/site-content`).then(r => {
      if (r.data?.about) setAbout(prev => ({ ...prev, ...r.data.about }));
    }).catch(err => console.error('Failed to load site content:', err));
  }, []);

  return (
    <footer data-testid="footer" className="bg-[#0C4A8C]">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-10 md:gap-8">
          <div className="md:col-span-1">
            <LogoFull size="nav" variant="dark" />
            <p className="text-[12px] mt-3 font-light" style={{ color: 'rgba(255,255,255,0.3)' }}>Skincare built for Indian men and Indian conditions.</p>
            <div className="flex gap-4 mt-5">
              <a href={about.instagram_url} target="_blank" rel="noopener noreferrer" data-testid="footer-instagram" className="text-white/30 hover:text-[#BAE6FD] transition-colors"><Instagram size={16} strokeWidth={1.5} /></a>
              <a href={`mailto:${about.email}`} data-testid="footer-email" className="text-white/30 hover:text-[#BAE6FD] transition-colors"><Mail size={16} strokeWidth={1.5} /></a>
            </div>
          </div>

          <div>
            <h4 className="eyebrow text-[#BAE6FD] mb-5">Shop</h4>
            <div className="flex flex-col gap-2.5">
              <Link to="/shop?category=face" data-testid="footer-face-care" className="text-[13px] text-white/35 hover:text-white/70 transition-colors font-light">Face Care</Link>
              <Link to="/shop?category=hair" data-testid="footer-hair-care" className="text-[13px] text-white/35 hover:text-white/70 transition-colors font-light">Hair Care</Link>
              <Link to="/shop?category=body" data-testid="footer-body-care" className="text-[13px] text-white/35 hover:text-white/70 transition-colors font-light">Body Care</Link>
            </div>
          </div>

          <div>
            <h4 className="eyebrow text-[#BAE6FD] mb-5">Learn</h4>
            <div className="flex flex-col gap-2.5">
              <Link to="/science" data-testid="footer-science" className="text-[13px] text-white/35 hover:text-white/70 transition-colors font-light">The Science</Link>
              <Link to="/about" data-testid="footer-about" className="text-[13px] text-white/35 hover:text-white/70 transition-colors font-light">About</Link>
            </div>
          </div>

          <div>
            <h4 className="eyebrow text-[#BAE6FD] mb-5">Help</h4>
            <div className="flex flex-col gap-2.5">
              <Link to="/refund" data-testid="footer-returns" className="text-[13px] text-white/35 hover:text-white/70 transition-colors font-light">Shipping & Returns</Link>
              <a href={`mailto:${about.email}`} data-testid="footer-contact" className="text-[13px] text-white/35 hover:text-white/70 transition-colors font-light">Contact</a>
            </div>
          </div>

          {/* Newsletter Signup */}
          <FooterNewsletter />
        </div>

        <div className="mt-12 pt-6 flex flex-col md:flex-row items-center justify-between gap-3" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
          <p className="text-[11px] font-light tracking-wide" style={{ color: 'rgba(255,255,255,0.2)' }}>&copy; 2024 TRUFORM MEN &middot; truformmen.com</p>
          <div className="flex items-center gap-3">
            <span className="text-[10px] text-white/15 uppercase tracking-[1px]">Visa</span>
            <span className="text-[10px] text-white/15 uppercase tracking-[1px]">RuPay</span>
            <span className="text-[10px] text-white/15 uppercase tracking-[1px]">UPI</span>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default function Layout({ children }) {
  return (
    <div className="min-h-screen bg-white">
      <AnnouncementBar />
      <Navbar />
      <main>{children}</main>
      <Footer />
    </div>
  );
}

